import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { isEmpty, cloneDeep } from 'lodash-es';

import Header from '../Header';
import SavedFilters from '../SavedFilters';
import LocationFilter from '../LocationFilter';
import YearFilter from '../YearFilter';
import MakeManufacturerFilter from '../MakeManufacturerFilter';
import OdometerFilter from '../OdometerFilter';
import TradeFilter from '../TradeFilter';
import VehicleFilter from '../VehicleFilter';
import ConditionFilter from '../ConditionFilter';
import ConfirmModalContainer from '../ConfirmModalContainer';
import MixpanelConfig from '../../../_shared/constants/mixpanelConfig';

import {
  setActiveFilter,
  initializeActiveFilter,
  setActiveFilterFromUrl,
  setPreviewCriteria,
  setActiveFilterMetaData
} from '../../../redux/slices/filters';
import {
  CacheKeys,
  YEAR,
  ODOMETER,
  FilterType,
  FilterPillsList,
  MixpanelFilterName
} from '../../../_shared/constants/filters';
import {
  objectToQueryString,
  queryStringToObject
} from '../../../_shared/utils/url';

import { sessionStorageService } from '../../../services/StorageService';
import VehicleService from '../../../services/VehicleService';
import FilterService from '../../../services/FilterService';
import MixpanelService from '../../../services/MixpanelService';
import SettingsService from '../../../services/SettingsService';

import useListMetaData from '../../../_shared/hooks/list/useListMetaData';
import useTradeMetaData from '../../../_shared/hooks/trade/useTradeMetaData';
import useManufacturerList from '../../../_shared/hooks/vehicle/useManufacturerList';
import useDealerDetails from '../../../_shared/hooks/organization/useDealerDetails';
import useNavHeight from '../../../_shared/hooks/useNavHeight';
import SystemConfigService from '../../../services/SystemConfigService';
import ReducerConstants from '../../../_shared/constants/reducers';

import { FilterNames, TradeStateName } from '../../../_shared/types/trade';
import { ListVariantSource } from '../../../_shared/types/auction';
import { FilterContext } from '../internal';

import {
  FilterWrapper,
  Content,
  Footer,
  SaveButton,
  OverlayWrapper,
  Overlay
} from './Styles';

const Filter = ({
  userLists,
  tradeState,
  companyId,
  isEditing,
  setIsEditing,
  filtersRef,
  refetchFilters,
  isFilterReset,
  setIsFilterReset,
  filterPillName,
  isFiltersListFetched
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  const [
    activeFilter,
    filterCriteria,
    previewCriteria,
    configurations,
    listVariant
  ] = useSelector(
    state => [
      state.filter.activeFilter,
      state.filter.filterCriteria,
      state.filter.previewCriteria,
      state[ReducerConstants.AUTH]?.system?.configurations,
      state.ui.layout.listVariant
    ],
    shallowEqual
  );

  const { search, pathname } = location;
  const savedFilters = useMemo(() => {
    return FilterService.removeDGNFilters(userLists?.summaries);
  }, [userLists?.summaries]);

  const systemConfigService = SystemConfigService.get(configurations);
  const distanceConfigurations = useMemo(
    () => systemConfigService.getDistanceConfigurations(),
    [systemConfigService]
  );
  const vehicleService = VehicleService.get();
  const mixpanelService = MixpanelService.get();

  const { data: listMetaData } = useListMetaData();
  const { data: tradeMetaData } = useTradeMetaData();
  const { data: manufacturersList } = useManufacturerList();
  const { data: dealerDetails } = useDealerDetails({ companyId });
  const [enableNotification, setEnableNotification] = useState(true);
  const [canEditFilters, setCanEditFilters] = useState(true);

  const [filterMetaData, setFilterMetaData] = useState({
    locations: {},
    conditions: [],
    manufacturers: {},
    models: {},
    buyerAddresses: [],
    distances: [],
    fuelTypes: {},
    transmissionTypes: {},
    yearRange: {
      start: 0,
      end: YEAR.max + 1
    },
    mileageRange: {
      start: ODOMETER.min,
      end: ODOMETER.max
    },
    tradeConditions: {
      isFranchiseDealer: false,
      isInStock: false,
      isFloorPriceMet: false
    },
    showDistance: false
  });

  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [isModelsLoading, setIsModelsLoading] = useState(false);

  const dispatchFilterMetaData = metaData => {
    dispatch(setActiveFilterMetaData(cloneDeep(metaData)));
  };

  // Build request for filter preview
  const buildListCreateRequest = (criteria, key, filterMetaDataUpdates) => {
    const updatedState = {
      ...filterMetaData,
      ...filterMetaDataUpdates,
      [key]: criteria
    };

    const listRequest = FilterService.createListPreviewRequest(
      updatedState,
      criteria,
      key
    );

    if (isFilterReset) {
      mixpanelService.track(MixpanelConfig.names.removeFilterPill, {
        select_pill: MixpanelFilterName[filterPillName],
        trade_state: TradeStateName[tradeState],
        list_type: ListVariantSource[listVariant]
      });
    } else {
      mixpanelService.trackFilterApply(
        {
          [key]: criteria
        },
        ListVariantSource[listVariant]
      );
    }

    dispatch(setPreviewCriteria(listRequest));
    dispatchFilterMetaData(updatedState);
    setFilterMetaData(updatedState);

    setIsEditing(true);
  };

  const updateSelectedManufacturers = (
    currentFilterMetaData,
    updatedManufacturers,
    filterKey
  ) => {
    // display models filter
    setIsModelsLoading(true);

    // Get year
    const { start, end } = currentFilterMetaData.yearRange;

    const { models } = currentFilterMetaData;

    const selectedManufacturerId = Object.keys(updatedManufacturers).filter(
      manufacturerId => updatedManufacturers[manufacturerId].isSelected
    );

    const selectedModels = FilterService.getSelectedModels(
      models,
      updatedManufacturers
    );

    // set is unselected to false for manufacturers in models not present
    // in updated manufacturers
    Object.keys(models).forEach(id => {
      if (!selectedManufacturerId.includes(id)) {
        delete models[id];
      }
    });

    setFilterMetaData(prevState => {
      return {
        ...prevState,
        models
      };
    });

    FilterService.loadModels(
      start,
      end,
      updatedManufacturers,
      selectedModels,
      vehicleService.getModelList
    ).then(_models => {
      const newState = {
        ...filterMetaData,
        models: { ..._models }
      };
      dispatchFilterMetaData(newState);
      setIsModelsLoading(false);
      buildListCreateRequest(updatedManufacturers, filterKey, {
        models: { ..._models }
      });
    });
  };

  const updateSelectedModels = (
    currentFilterMetaData,
    updatedModels,
    filterKey
  ) => {
    const copyManufacturers = cloneDeep(currentFilterMetaData.manufacturers);

    const unSelectedModels = [];

    Object.keys(updatedModels).forEach(manufacturerId => {
      // set manufacturers with no model selected to false
      if (!updatedModels[manufacturerId].isSelected) {
        copyManufacturers[manufacturerId].isSelected = false;
        unSelectedModels.push(manufacturerId);
      }
    });

    // remove manufacturers with no models selected
    unSelectedModels.forEach(id => delete updatedModels[id]);
    const newState = {
      ...filterMetaData,
      manufacturers: copyManufacturers,
      models: updatedModels
    };

    dispatchFilterMetaData(newState);
    buildListCreateRequest(updatedModels, filterKey, {
      manufacturers: copyManufacturers,
      models: updatedModels
    });
  };

  const updateYearFilter = (currentFilterMetaData, start, end, filterKey) => {
    const { manufacturers } = currentFilterMetaData;

    FilterService.loadModels(
      start,
      end,
      manufacturers,
      undefined,
      vehicleService.getModelList
    ).then(_models => {
      buildListCreateRequest({ start, end }, filterKey, {
        models: FilterService.applyModelsSelection(
          _models,
          filterMetaData.models
        )
      });
    });
  };

  const updateShowDistanceProvince = showDistance => {
    const updatedState = {
      ...filterMetaData,
      showDistance
    };
    const listRequest = FilterService.createListPreviewRequest(
      updatedState,
      showDistance,
      'showDistance'
    );

    dispatch(setPreviewCriteria(listRequest));
    dispatchFilterMetaData(updatedState);
    buildListCreateRequest(showDistance, 'showDistance');
  };

  const handleFilterChange = filter => {
    dispatch(setActiveFilter(filter));
    setIsEditing(false);
    const urlParams = new URLSearchParams(search);

    history.push({
      search: objectToQueryString({
        ...Object.fromEntries(urlParams),
        listId: filter.id
      })
    });

    FilterService.setCachedTradeFilterId(tradeState, filter.id);

    if (filter.id !== FilterNames.all.id) {
      mixpanelService.trackSelectFilter(
        filter.id,
        tradeState,
        ListVariantSource[listVariant]
      );
    }
  };

  const clearFilters = filter => {
    setCanEditFilters(true);
    setIsEditing(false);
    handleFilterChange(filter);
  };

  // updates the user saved filter list on create or update filter
  // when create pushes the new saved filter into the saved filter
  // list and sets it as the current active filter
  const updateFilterList = useCallback(
    async newFilter => {
      await refetchFilters();
      if (activeFilter.id === FilterNames.all.id) {
        savedFilters.push({
          id: newFilter.id,
          title: newFilter.title,
          subtitle: null,
          count: 0,
          type: 5,
          enabled: true,
          enableNotification: newFilter.enableNotification
        });
        mixpanelService.trackFilter('create', {
          ...filterMetaData,
          tradeState,
          listType: ListVariantSource[listVariant]
        });
        dispatch(setActiveFilter(savedFilters[savedFilters.length - 1]));
        FilterService.sortUserFilterList(savedFilters);
        FilterService.setCachedTradeFilterId(tradeState, newFilter.id);
        const urlParams = new URLSearchParams(search);
        history.push({
          search: objectToQueryString({
            ...Object.fromEntries(urlParams),
            listId: newFilter.id
          })
        });
      } else {
        mixpanelService.trackFilter('update', {
          ...filterMetaData,
          tradeState,
          listType: ListVariantSource[listVariant]
        });
      }

      setIsEditing(false);
    },
    [
      activeFilter.id,
      dispatch,
      filterMetaData,
      mixpanelService,
      savedFilters,
      tradeState,
      setIsEditing,
      history,
      search,
      refetchFilters,
      listVariant
    ]
  );

  // variable responsible for managing if the filters should be retrieved from the last state or not
  const [keepFilter, setKeepFilter] = useState(false);
  useEffect(() => {
    const shouldKeepFilters = Boolean(location.state?.keepFilters);

    // Clear current router state
    if (shouldKeepFilters !== keepFilter) {
      const newLocationState = cloneDeep(location.state ?? {});
      delete newLocationState.keepFilters;
      history.replace({ state: { ...newLocationState } });
      setKeepFilter(shouldKeepFilters);
    }
  }, [pathname]); //eslint-disable-line

  // Initialize active filter
  useEffect(() => {
    if (
      isFiltersListFetched &&
      !isEmpty(savedFilters) &&
      !location.state?.keepFilters
    ) {
      sessionStorageService.setItem(CacheKeys.filterList, savedFilters);

      dispatch(initializeActiveFilter(tradeState));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFiltersListFetched, dispatch, tradeState]);

  // Update active filter when query Param listid changes
  useEffect(() => {
    if (search && !isEmpty(savedFilters) && !location.state?.keepFilters) {
      const filterId = queryStringToObject(search);

      const filter = savedFilters.find(filterItem => {
        return filterItem.id === filterId?.listId;
      });

      dispatch(setActiveFilterFromUrl(filter ?? FilterNames.all));

      if (!filter) {
        const queryParams = new URLSearchParams(search);
        queryParams.delete('listId');
        history.replace({
          search: objectToQueryString({ ...Object.fromEntries(queryParams) })
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, dispatch, history]);

  // Resets filter state when a tradestate is detected
  // specifically disregards any user edits i.e manual filters
  useEffect(() => {
    if (tradeState && !location.state?.keepFilters) {
      setIsEditing(false);
      dispatch(setPreviewCriteria({}));
    }
  }, [dispatch, setIsEditing, tradeState]); //eslint-disable-line

  // Monitors for filter criteria change and proceeds to populate
  // filters by loading metadata and parsing criteria data into filters ui
  useEffect(() => {
    function canEdit(_filterCriteria, activeFilterId) {
      const hasCriteria = _filterCriteria?.criteria;
      const isAllList = activeFilterId === FilterNames.all.id;
      return isAllList || hasCriteria;
    }

    if (
      listMetaData &&
      tradeMetaData &&
      manufacturersList &&
      dealerDetails &&
      !isEmpty(filterCriteria) &&
      activeFilter.id === filterCriteria.id
    ) {
      if (!canEdit(filterCriteria, activeFilter.id)) {
        setCanEditFilters(false);
        return;
      }

      const metaData = FilterService.loadMetaData(
        listMetaData,
        manufacturersList,
        dealerDetails,
        tradeMetaData,
        distanceConfigurations
      );

      FilterService.populateFilters(
        metaData,
        filterCriteria,
        vehicleService.getModelList,
        tradeState,
        setFilterMetaData,
        dispatchFilterMetaData
      );
      setCanEditFilters(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    listMetaData,
    tradeMetaData,
    manufacturersList,
    dealerDetails,
    filterCriteria,
    activeFilter,
    distanceConfigurations
  ]);

  useEffect(() => {
    if (isFilterReset) {
      if (filterPillName === FilterPillsList.ModelYear) {
        updateYearFilter(filterMetaData, 0, YEAR.max + 1, FilterType.YearRange);
      } else if (filterPillName === FilterPillsList.Make) {
        const updatedMakes = FilterService.removeFilterPill(
          filterPillName,
          filterMetaData
        );
        updateSelectedManufacturers(
          filterMetaData,
          updatedMakes.filterData,
          updatedMakes.filterKey
        );
      } else if (filterPillName === FilterPillsList.Model) {
        const updatedModels = FilterService.removeFilterPill(
          filterPillName,
          filterMetaData
        );
        updateSelectedModels(
          filterMetaData,
          updatedModels.filterData,
          updatedModels.filterKey
        );
      } else {
        const updatedFilter = FilterService.removeFilterPill(
          filterPillName,
          filterMetaData
        );
        buildListCreateRequest(
          updatedFilter.filterData,
          updatedFilter.filterKey
        );
      }
    }
    setIsFilterReset(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFilterReset, filterPillName, previewCriteria, filterMetaData]);

  const contextValue = {
    filterMetaData,
    updateFilter: buildListCreateRequest,
    updateSelectedManufacturers,
    updateSelectedModels,
    updateYearFilter,
    updateShowDistanceProvince
  };

  const navHeight = useNavHeight();
  const filtersSpanHeight =
    filtersRef?.current?.offsetHeight + navHeight - window.innerHeight >= 0;

  const [settings, setSettings] = useState();
  const settingsService = SettingsService.get();

  const getSettings = useCallback(async () => {
    const settingsResponse = await settingsService.getNotificationSettings();

    setSettings(settingsResponse);
  }, [settingsService]);

  useEffect(() => {
    getSettings();
  }, [getSettings]);

  return (
    <FilterContext.Provider value={contextValue}>
      <FilterWrapper>
        <Header
          isEditing={isEditing}
          clearFilter={clearFilters}
          activeFilter={activeFilter}
        />

        <Content
          variant={listVariant}
          isEditing={isEditing}
          filtersSpanHeight={filtersSpanHeight}
        >
          <SavedFilters
            defaultValue={activeFilter}
            options={savedFilters}
            onChange={handleFilterChange}
            isEditing={isEditing}
            refetchFilters={refetchFilters}
            tradeState={tradeState}
            setEnableNotification={setEnableNotification}
            settings={settings}
            setSettings={setSettings}
            getSettings={getSettings}
          />
          <OverlayWrapper canEdit={canEditFilters}>
            {!canEditFilters && <Overlay />}
            <LocationFilter />
            <YearFilter />
            <MakeManufacturerFilter isModelsLoading={isModelsLoading} />
            <OdometerFilter />
            <VehicleFilter />
            <ConditionFilter />
            <TradeFilter tradeState={tradeState} />
          </OverlayWrapper>
          {isEditing && (
            <Footer variant={listVariant} filtersSpanHeight={filtersSpanHeight}>
              <SaveButton
                testId="save-button"
                onClick={() => setIsConfirmationModalOpen(true)}
              >
                <FormattedMessage id="list.filters.save" />
              </SaveButton>
            </Footer>
          )}
        </Content>

        <ConfirmModalContainer
          isOpen={isConfirmationModalOpen}
          setIsOpen={setIsConfirmationModalOpen}
          updateFilterList={updateFilterList}
          activeFilter={activeFilter}
          filterList={savedFilters}
          tradeState={tradeState}
          enableNotification={enableNotification}
          setEnableNotification={setEnableNotification}
          settings={settings}
          getSettings={getSettings}
        />
      </FilterWrapper>
    </FilterContext.Provider>
  );
};

export default Filter;
